import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['bg-[#003456]', 'bg-[#B87333]', 'bg-[#023F63]', 'bg-[#003456]', 'bg-[#8a5a2b]'];

const MARK_QUOTE = `By chance, I found Chinook Manufacturing, LyphLine's sister company, through a random internet search. After reading through the website, I was compelled to call.

I was intrigued by the fact that a Canadian company was manufacturing freeze-dryers. I was not disappointed. After several hours of chatting, I was thoroughly impressed by the team's depth of knowledge and the gains they had made in the design and production of their machines.

In my opinion, Chinook Manufacturing was light-years ahead of other competitors in the marketplace. Being the owner of over 12 Harvest Right machines, 6 of which did not work at all, and the other 6 that worked sporadically, I was quite familiar with the frustrations in the industry. Bad design, inadequate customer service, and poorly manufactured products almost buried me financially...until I met the owners at Chinook Manufacturing. A victim of the same frustrations, they figured out how to build a better mouse trap and fixed the old mouse traps. If it had not been for that fateful phone call, I would be out of the freeze-drying business. LyphLine was instrumental in bringing all my malfunctioning machines back to life—and even took the ones that worked and made them perform better.

I am now able to create a better product, making it faster with fewer to no breakdowns. In manufacturing, time is money. Breakdowns are costly and expensive. Those days are behind us.

LyphLine is a lifesaver. They are fast and prompt with a world-class team of technicians. They have great customer service with fair pricing. We now have a technical "go to" company that can help us virtually 24/7 with wifi connectivity that wasn't offered by the other company.

Without reservation, I highly recommend contacting LyphLine regardless of what machine you own. Their depth of knowledge and talented team can help you.`;

const FALLBACK = [
  { name: 'Mark', role: 'Freeze dryer business owner', location: '', initials: 'M', rating: 5, quote: MARK_QUOTE },
  { name: 'Dennis', role: 'Business Owner', location: '', initials: 'D', rating: 5,
    quote: "I appreciated the honest assessment and quick turnaround. The upgraded parts have made a noticeable difference in reliability. Highly recommend for anyone needing repairs." },
];

const initialsOf = (name) => {
  const parts = (name || '').split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0]).toUpperCase();
};

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < n ? 'text-[#CE8946] fill-[#CE8946]' : 'text-slate-300'}`} />
    ))}
  </div>
);

const Byline = ({ t, i }) => (
  <figcaption className="flex items-center gap-3 mt-6 pt-5 border-t border-slate-100">
    <span className={`w-11 h-11 rounded-full ${t.color || COLORS[i % COLORS.length]} text-white font-bold flex items-center justify-center flex-shrink-0`}>{t.initials || initialsOf(t.name)}</span>
    <div className="leading-tight">
      <p className="text-[#003456] font-bold text-sm">{t.name}</p>
      {[t.role, t.location].filter(Boolean).length > 0 && (
        <p className="text-slate-500 text-xs">{[t.role, t.location].filter(Boolean).join(' · ')}</p>
      )}
    </div>
  </figcaption>
);

export const Testimonials = ({ bg = 'bg-[#F1F5F9]' }) => {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API_URL}/api/site-testimonials`)
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setItems(r.data); })
      .catch(() => { /* keep fallback */ });
  }, []);

  const featured = items[0];
  const rest = items.slice(1, 7);
  // Treat long testimonials as a full-width featured story.
  const featuredIsLong = featured && (featured.quote || '').length > 320;

  return (
    <section className={`py-20 section-padding ${bg}`} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-12">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">What owners say</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Machines saved, businesses back online</h2>
          <p className="text-slate-600 mt-3">Real outcomes from owners who chose a rebuild over a costly replacement.</p>
        </div>

        {featured && (
          <figure
            className={`bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 hover:shadow-lg transition-shadow ${featuredIsLong ? 'mb-6' : ''}`}
            data-testid="testimonial-featured"
          >
            <div className="flex items-center justify-between">
              <Quote className="h-9 w-9 text-[#CE8946]/30" />
              <Stars n={featured.rating || 5} />
            </div>
            <blockquote className="text-slate-700 text-sm sm:text-[15px] leading-relaxed mt-5 whitespace-pre-line">
              {featured.quote}
            </blockquote>
            <Byline t={featured} i={0} />
          </figure>
        )}

        {rest.length > 0 && (
          <div className={`grid gap-6 ${rest.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {rest.map((t, i) => (
              <figure key={t.id || i} className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col hover:shadow-lg transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between">
                  <Quote className="h-8 w-8 text-[#CE8946]/30" />
                  <Stars n={t.rating || 5} />
                </div>
                <blockquote className="text-slate-700 text-sm leading-relaxed mt-4 flex-1 whitespace-pre-line">"{t.quote}"</blockquote>
                <Byline t={t} i={i + 1} />
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
